import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import CloseCircleIcon from '@/assets/icon/CloseCircleIcon';
import CloseCircleNativeIcon from '@/assets/icon/CloseCircleNativeIcon';
import SearchIcon from '@/assets/icon/SearchIcon';

import PlainIconButton from '../PlainIconButton/PlainIconButton';
import {
  IconsContainer,
  InputWrapper,
  OverlayDiv,
  SearchContainer,
  StyledInput,
} from './SearchInput.styles';
import { SearchInputProps } from './SearchInput.types';

type SearchInputRef = {
  focus: () => void;
  setSelectionToEnd: () => void;
};

const SearchInput = forwardRef<SearchInputRef, SearchInputProps>(
  (
    {
      // Value and state
      value,
      defaultValue,
      placeholder,
      label,
      'aria-label': ariaLabel,

      // Icons
      showSearchIcon = true,
      showClearIcon = true,

      // Callbacks
      onChange,
      onClick,
      onFocus,
      onBlur,
      onEnter,
      onClear,

      // Styling
      className,
      sx,
      containerSx,
      inputSx,

      // Behavior
      autoFocus = false,
      disabled = false,
      isSearchbarWhite,

      readOnly = false,
      // Custom render props
      renderLeftIcon,
      renderRightIcons,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      setSelectionToEnd: () => {
        const el = inputRef.current;
        if (el) {
          const len = el.value.length;
          el.setSelectionRange(len, len);
        }
      },
    }));

    const currentValue =
      onChange !== undefined ? (value ?? '') : (value ?? defaultValue ?? '');

    const inputPlaceholder = placeholder;
    const inputAriaLabel = ariaLabel || label || placeholder;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    };

    const handleClick = () => {
      onClick?.();
    };

    const handleFocus = () => {
      onFocus?.();
    };

    const handleBlur = () => {
      onBlur?.();
    };

    const handleInputWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'INPUT' && !target.closest('input')) {
        inputRef.current?.focus();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onEnter?.(currentValue);
      }
    };

    const handleClear = () => {
      onChange?.('');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      onClear?.();
    };

    const combinedSx = (containerSx ?? sx) as never;

    return (
      <SearchContainer
        className={className}
        sx={combinedSx}
        isSearchbarWhite={isSearchbarWhite}
      >
        {readOnly && (
          <OverlayDiv
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClick();
            }}
          />
        )}
        {/* Left Search Icon */}
        {renderLeftIcon
          ? renderLeftIcon()
          : showSearchIcon && <SearchIcon width={17} height={17} />}

        {/* Input Field with Animated Placeholder */}
        <InputWrapper ref={inputWrapperRef} onClick={handleInputWrapperClick}>
          <StyledInput
            name="Search-Input"
            ref={inputRef}
            {...(inputPlaceholder !== undefined && {
              placeholder: inputPlaceholder,
            })}
            value={currentValue}
            onChange={handleChange}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus={autoFocus}
            readOnly={readOnly}
            disabled={disabled}
            aria-label={inputAriaLabel}
            sx={inputSx as never}
          />
        </InputWrapper>

        {/* Right Icons */}
        {renderRightIcons ? (
          renderRightIcons()
        ) : (
          <IconsContainer>
            {showClearIcon && currentValue.length > 0 && (
              <PlainIconButton
                size="small"
                onClick={handleClear}
                disabled={disabled}
                aria-label="Clear"
              >
                {isSearchbarWhite ? (
                  <CloseCircleNativeIcon />
                ) : (
                  <CloseCircleIcon />
                )}
              </PlainIconButton>
            )}
          </IconsContainer>
        )}
      </SearchContainer>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
